import { useEffect, useMemo, useState } from "react";
import { InputDropdown } from "@onengine/component-library";
import type { DropdownItem } from "@onengine/component-library";
import { useScopeOptions } from "@/hooks/access/useScopeOptions";
import type { ScopeOption, WritableBox } from "@/api/access/userScope";
import { NO_OPTIONS, OPTIONS_FAILED, OPTIONS_NO_MATCH } from "./setScopeCopy";

export interface ScopeValuePickerProps {
  tenantId: string;
  tenantName: string;
  box: WritableBox;
  /** The box's name, used for the field label, the placeholder and the empty-state line. */
  boxLabel: string;
  value: string[];
  onChange: (ids: string[]) => void;
  error?: string;
  /**
   * An "everything" choice listed first. Picking it replaces the other picks, and picking
   * anything else drops it, so the field never says "all of them, and also these two".
   */
  allOption?: { value: string; label: string };
  /** Rewords one option's label, e.g. to say a client comes from the person's client teams. */
  labelFor?: (value: string, label: string) => string;
  /** A picked value that can't be unpicked (a team client): drawn checked and disabled. */
  isLocked?: (value: string) => boolean;
}

/**
 * The picker for one scope box in one entity.
 *
 * It remembers every option it has shown, because the library only draws a chip for a value
 * still present in `items`: without that, a value picked before a search loses its chip while
 * staying in the saved set (the same defect the Add People picker had).
 */
export function ScopeValuePicker({
  tenantId,
  tenantName,
  box,
  boxLabel,
  value,
  onChange,
  error,
  allOption,
  labelFor,
  isLocked,
}: ScopeValuePickerProps) {
  const { options, setSearch, isLoading, isError, isEmpty, isNoMatch, hasMore, loadMore } =
    useScopeOptions(tenantId, box, true);
  const [seen, setSeen] = useState<Map<string, ScopeOption>>(new Map());

  useEffect(() => {
    if (options.length === 0) return;
    setSeen((previous) => {
      // Returning the same Map when nothing is new keeps this from re-rendering on every
      // answer, which would re-run this effect against a fresh array and never settle.
      if (options.every((option) => previous.has(option.value))) return previous;
      const next = new Map(previous);
      options.forEach((option) => next.set(option.value, option));
      return next;
    });
  }, [options]);

  const items = useMemo<DropdownItem[]>(() => {
    const shown = [...options];
    const inResults = new Set(options.map((option) => option.value));
    value.forEach((id) => {
      const option = seen.get(id);
      if (option && !inResults.has(id)) shown.push(option);
    });
    const listed = shown.map((option) => ({
      value: option.value,
      label: labelFor ? labelFor(option.value, option.label) : option.label,
      disabled: value.includes(option.value) && !!isLocked?.(option.value),
    }));
    return allOption ? [allOption, ...listed] : listed;
  }, [options, value, seen, allOption, labelFor, isLocked]);

  const handleChange = (ids: string[]) => {
    if (allOption && ids.includes(allOption.value) && !value.includes(allOption.value)) {
      return onChange([allOption.value]);
    }
    const next = allOption ? ids.filter((id) => id !== allOption.value) : ids;
    // The library disables only a locked row's checkbox: its chip's remove button and the row's
    // label still press it off, so any change that drops a locked value is ignored here.
    if (value.some((id) => isLocked?.(id) && !next.includes(id))) return;
    onChange(next);
  };

  // The dropdown has no empty-state slot, so "this entity has none" and "your search matched
  // nothing" are said in the hint instead of leaving an empty list to be read as either.
  const stateHint = isEmpty
    ? NO_OPTIONS(boxLabel, tenantName)
    : isNoMatch
      ? OPTIONS_NO_MATCH
      : undefined;

  return (
    <InputDropdown
      mode="multi"
      label={boxLabel}
      placeholder={`Search ${boxLabel.toLowerCase()}…`}
      searchable
      searchPlaceholder={`Search ${boxLabel.toLowerCase()}…`}
      loading={isLoading}
      items={items}
      value={value}
      onChange={handleChange}
      onSearch={setSearch}
      hasMore={hasMore}
      onLoadMore={loadMore}
      hint={stateHint}
      error={isError ? OPTIONS_FAILED : (error ?? false)}
    />
  );
}
